/**
 * Fan info: één inline bewerkbaar paneel in de rechterkolom (15-10-2026, Jordy).
 *
 * Geen vensters meer: "Edit info", "Add info" en "Notes" zitten hier samen.
 * Vast bovenaan: bijnaam + echte naam, tijdzone, laatst actief, status.
 * Daaronder de overige velden, ingevuld eerst en leeg daaronder (invulbaar).
 * Opslaan gebeurt bij het verlaten van een veld, via de bestaande routes
 * (zie lib/fanInfo.ts); daarna ververst het gesprek en schuift een net
 * ingevuld veld omhoog.
 */
import { useState } from 'react'
import type { ReactNode } from 'react'
import { StandTekst, Veld } from './InlineVeld'
import type { Stand } from './InlineVeld'
import { api } from '../lib/api'
import { indeling, nieuwEigenVeld, eigenVeldWeg, opslagVoor } from '../lib/fanInfo'
import type { Info, Opslag, Regel } from '../lib/fanInfo'

export function FanInfoPaneel({ conversationId, info, notities, bijnaam, fanId, lokaleTijd,
                                laatstActief, status, onSaved, notitieFocus, bodem, modelBoven, modelMidden,
                                notitiesElders, notitiesBlok }: {
  conversationId: number | string
  info: Info | null | undefined
  notities: string | null | undefined
  bijnaam: string
  fanId: string
  /** null = onbekend (geen tijdzone): "—" met een kleine hint. */
  lokaleTijd: string | null
  laatstActief: string
  status: string
  onSaved: () => void
  /** Ophogen = de cursor in "Important notes" zetten (vanuit de notitiebalk). */
  notitieFocus?: number
  /** 16-10-2026 (Jordy): vast onderaan (Models this fan is speaking to,
   *  Subscription, Spending behavior); scrollt niet mee. */
  bodem?: ReactNode
  /** 18-10-2026 (Jordy): de modelkolom LINKS naast Fan info — vast boven
   *  (naam, Persona guide, Do's/Don'ts) en een eigen scrollend midden. */
  modelBoven?: ReactNode
  modelMidden?: ReactNode
  /** 18-10-2026 (Jordy): Important notes niet als gewoon veld in de lijst (zie notitiesBlok). */
  notitiesElders?: boolean
  /** 18-10-2026 (Jordy): het gele Important notes-blok, direct onder Status. */
  notitiesBlok?: ReactNode
}) {
  const ind = indeling(info, notities)
  const [nieuwLabel, setNieuwLabel] = useState('')
  const [nieuwWaarde, setNieuwWaarde] = useState('')
  const [nieuwStand, setNieuwStand] = useState<Stand>('')
  const [nieuwFout, setNieuwFout] = useState('')

  const stuur = async (o: Opslag) => {
    await api.patch(o.pad, o.body)
    onSaved()
  }
  const sla = (r: Regel) => (w: string) => stuur(opslagVoor(conversationId, info, r, w))
  const voegToe = async () => {
    const o = nieuwEigenVeld(conversationId, info, nieuwLabel, nieuwWaarde)
    if (!o) { setNieuwStand('error'); setNieuwFout('Give the field a name.'); return }
    setNieuwStand('saving'); setNieuwFout('')
    try {
      await stuur(o)
      setNieuwLabel(''); setNieuwWaarde(''); setNieuwStand('saved')
    } catch (e) {
      setNieuwStand('error'); setNieuwFout(String((e as Error)?.message || e))
    }
  }

  const model = !!(modelBoven || modelMidden)
  return (
    // 16-10-2026 (Jordy): boven vast, midden scrollt, onder vast (binnen de
    // vaste paneelhoogte). 18-10-2026: MODEL links, FAN rechts, elk met een
    // eigen vaste bovenkant en een eigen scrollend midden; onder over de volle
    // breedte. Smaller dan ~1400 px staan ze onder elkaar (theme.css).
    <div className="fanindeling" data-testid="fan-info-paneel">
      <div className={model ? 'mfkolommen' : 'mfkolommen een'}>
      {model ? (
        <section className="mfkol" data-testid="model-kolom">
          <div className="boven" data-testid="model-vast">
            <span className="kolomkop">Model</span>
            {modelBoven}
          </div>
          <div className="midden" data-testid="model-midden">{modelMidden}</div>
        </section>
      ) : null}
      <section className="mfkol" data-testid="fan-kolom">
      {/* 1. Vast bovenaan, in deze volgorde. */}
      <div className="boven fanvast" data-testid="fan-info-vast">
        {model ? <span className="kolomkop">Fan</span> : null}
        <div className="mut2" style={{ fontSize: 11 }}>ID: <span className="mono">{fanId || '—'}</span></div>
        {/* 16-10-2026: compact, zodat het scrollende midden ruimte houdt. */}
        <div className="fanvast-rij">
          <Veld label="Nickname" waarde={bijnaam} testid="veld-nickname"
                opslaan={(w) => stuur({ pad: `/api/admin/conversations/${conversationId}/fan-dashboard-name`,
                                        body: { display_name: w } })} />
          <Veld label="Real name" waarde={ind.naam.waarde} testid="veld-info:name"
                opslaan={sla(ind.naam)} />
        </div>
        {/* 16-10-2026: tijdzone en de stand naast elkaar; lokale tijd klein erbij. */}
        <div className="fanvast-rij">
          <Veld label={ind.tijdzone.label} waarde={ind.tijdzone.waarde} testid="veld-info:timezone"
                opslaan={sla(ind.tijdzone)} />
          {/* 18-10-2026 (Jordy): label · waarde, netjes onder elkaar uitgelijnd. */}
          <div className="fanstand" data-testid="fan-stand">
            <span className="lbl">Local time</span>
            <span className="wrd" data-testid="fan-lokale-tijd">
              {lokaleTijd ? <b>{lokaleTijd}</b> : <>
                <b>—</b>
                <span className="hint">set location or timezone</span>
              </>}
            </span>
            <span className="lbl">Last active</span>
            <span className="wrd"><b>{laatstActief}</b></span>
            <span className="lbl">Status</span>
            <span className="wrd"><b>{status}</b></span>
          </div>
        </div>
      </div>

      {/* 2. Het midden scrollt: de rest, ingevuld eerst, leeg daaronder. */}
      <div className="midden" data-testid="fan-info-midden">
      {notitiesBlok ? <div className="fannotities" data-testid="fan-notities">{notitiesBlok}</div> : null}
      <div data-testid="fan-info-overig" style={{ display: 'grid', gap: 6 }}>
        {ind.overig.filter((r) => !(notitiesElders && r.soort === 'notes')).map((r) => (
          <Veld key={r.id} label={r.label} waarde={r.waarde} meerregelig={r.meerregelig}
                testid={`veld-${r.id}`} opslaan={sla(r)}
                focusSleutel={r.soort === 'notes' ? notitieFocus : undefined}
                extra={r.soort === 'eigen' ? (
                  <button type="button" className="linkbtn" title="Remove this custom field"
                          onClick={() => { void stuur(eigenVeldWeg(conversationId, info, Number(r.sleutel))) }}>
                    ✕
                  </button>
                ) : undefined} />
        ))}
      </div>

      {/* 3. Add info: een eigen veld erbij. */}
      <div className="fanveld" data-testid="fan-info-toevoegen" style={{ marginTop: 10 }}>
        <div className="row" style={{ alignItems: 'center', gap: 6 }}>
          <span className="mut2" style={{ fontSize: 11.5 }}>Add info</span>
          <span style={{ flex: 1 }} />
          <StandTekst stand={nieuwStand} fout={nieuwFout} />
        </div>
        <div className="row" style={{ gap: 6 }}>
          <input className="inp" placeholder="Field (e.g. Favourite drink)" value={nieuwLabel}
                 aria-label="New field name" onChange={(e) => setNieuwLabel(e.target.value)} />
          <input className="inp" placeholder="Value" value={nieuwWaarde} aria-label="New field value"
                 onChange={(e) => setNieuwWaarde(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') void voegToe() }} />
          <button className="btn ghost sm" type="button" onClick={() => void voegToe()}>Add</button>
        </div>
      </div>
      </div>
      </section>
      </div>

      {/* 3. Vast onderaan. */}
      {bodem ? <div className="onder" data-testid="fan-info-onder">{bodem}</div> : null}
    </div>
  )
}
